<script setup lang="ts">
import { reactive, watch, ref } from 'vue';
import { Core as YubinBangoCore } from 'yubinbango-core2';
import i18n from '@/lang/i18n';

const props = defineProps<{
  zipCode: string;
  province: string;
  address1: string;
  address2: string;
  errors: {
    zipcode: string;
    province: string;
    address1: string;
    address2: string;
  };
  windowInnerWidth: number;
}>();
const emit = defineEmits(['inputValue']);
const zip1 = ref('');
const zip2 = ref('');
const provinceValue = ref('');
const address1Value = ref('');
const address2Value = ref('');
const data = reactive({
  zip1: '',
  zip2: '',
  province: '',
  address1: '',
  address2: '',
});

const unwatchZipcode = watch(
  () => props.zipCode,
  (propsZipCode) => {
    const splitZipcode = propsZipCode?.split('-') ?? ['', ''];
    zip1.value = splitZipcode[0];
    zip2.value = splitZipcode[1];
    unwatchZipcode();
  }
);
const unwatchProvince = watch(
  () => props.province,
  (propsProvince) => {
    provinceValue.value = propsProvince;
    unwatchProvince();
  }
);
const unwatchAddress1 = watch(
  () => props.address1,
  (propsAddress1) => {
    address1Value.value = propsAddress1;
    unwatchAddress1();
  }
);
const unwatchAddress2 = watch(
  () => props.address2,
  (propsAddress2) => {
    address2Value.value = propsAddress2;
    unwatchAddress2();
  }
);

function autoCompleteAddress() {
  const zipCodeValue = zip1.value + zip2.value;
  if (!zipCodeValue.match(/^\d{3}-?\d{4}$/)) {
    provinceValue.value = '';
    address1Value.value = '';
    address2Value.value = '';
  }
  if (zipCodeValue.length == 7) {
    new YubinBangoCore(zipCodeValue, (addr: any) => {
      provinceValue.value = addr.region;
      address1Value.value = addr.locality + addr.street;
    });
  }
}

watch(zip1, () => {
  data.zip1 = zip1.value;
  emit('inputValue', data);
});
watch(zip2, () => {
  data.zip2 = zip2.value;
  emit('inputValue', data);
});
watch(provinceValue, () => {
  data.province = provinceValue.value;
  emit('inputValue', data);
});
watch(address1Value, () => {
  data.address1 = address1Value.value;
  emit('inputValue', data);
});
watch(address2Value, () => {
  data.address2 = address2Value.value;
  emit('inputValue', data);
});
</script>
<template>
  <v-row class="text-form pl-4 pr-3 mb-3">
    <v-col :cols="windowInnerWidth > 800 ? 2 : 3">
      <h6 class="pl-4 align-center">
        {{ i18n.global.t('pages.operationOffices.form.address.postCode') }}
      </h6>
    </v-col>
    <v-col cols="3">
      <v-text-field
        v-model="zip1"
        maxlength="3"
        @keyup="autoCompleteAddress"
        :error-messages="errors?.zipcode"
      />
    </v-col>
    <span style="height: 50px; line-height: 50px"> - </span>
    <v-col cols="4">
      <v-text-field
        v-model="zip2"
        maxlength="4"
        @keyup="autoCompleteAddress"
        :error="errors?.zipcode?.length ? true : false"
      />
    </v-col>
  </v-row>
  <v-row class="text-form pl-4 pr-3 mb-3">
    <v-col :cols="windowInnerWidth > 800 ? 2 : 3">
      <h6 class="pl-4 align-center">
        {{ i18n.global.t('pages.operationOffices.form.address.prefectures') }}
      </h6>
    </v-col>
    <v-col cols="4">
      <v-text-field
        v-model="provinceValue"
        maxlength="255"
        :error-messages="errors?.province"
      />
    </v-col>
  </v-row>
  <v-row class="text-form pl-4 pr-3 mb-3">
    <v-col :cols="windowInnerWidth > 800 ? 2 : 3">
      <h6 class="pl-4">
        {{ i18n.global.t('pages.operationOffices.form.address.address1') }}
      </h6>
    </v-col>
    <v-col :cols="windowInnerWidth > 800 ? 10 : 9">
      <v-text-field
        v-model="address1Value"
        maxlength="255"
        :error-messages="errors?.address1"
      />
    </v-col>
  </v-row>
  <v-row class="text-form pl-4 pr-3 mb-3">
    <v-col :cols="windowInnerWidth > 800 ? 2 : 3">
      <h6 class="pl-4">
        {{ i18n.global.t('pages.operationOffices.form.address.address2') }}
      </h6>
    </v-col>
    <v-col :cols="windowInnerWidth > 800 ? 10 : 9">
      <v-text-field
        v-model="address2Value"
        maxlength="255"
        :error-messages="errors?.address2"
      />
    </v-col>
  </v-row>
</template>
